class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;

    this.onOverlayClick = this.close.bind(this);
    this.onKeyUp = (evt) => evt.code === 'Escape' && this.close();

    // ✅ debounce p/ evitar spam de eventos
    this.__apxMutRAF = null;

    // ✅ flag p/ evitar postRender duplicado (renderContents -> open)
    this.__apxJustRendered = false;

    this.addEventListener('keyup', this.onKeyUp);

    // bind inicial
    this.attachOverlayListener();
    this.setHeaderCartIconAccessibility();

    // garante acessibilidade do summary quando existir
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
  }

  // =========================
  // Helpers
  // =========================
  notifyCartMutated() {
    // ✅ 1 evento por frame (evita loop e “Carregando…” eterno)
    if (this.__apxMutRAF) return;

    this.__apxMutRAF = requestAnimationFrame(() => {
      this.__apxMutRAF = null;
      try {
        window.dispatchEvent(new CustomEvent('apx:cart-mutated'));
      } catch (e) {}
    });
  }

  attachOverlayListener() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    // evita duplicar listener quando o drawer re-renderiza
    if (overlay.__apx_bound_overlay) return;
    overlay.__apx_bound_overlay = true;

    overlay.addEventListener('click', this.onOverlayClick);
  }

  updateEmptyState(parsedState) {
    if (!parsedState || typeof parsedState.item_count !== 'number') return;

    const isEmpty = parsedState.item_count === 0;

    this.classList.toggle('is-empty', isEmpty);

    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', isEmpty);
  }

  retrapFocusIfActive() {
    if (!this.classList.contains('active')) return;

    try {
      removeTrapFocus(this.activeElement);
    } catch (e) {}

    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');

    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

    if (containerToTrapFocusOn && focusElement) {
      trapFocus(containerToTrapFocusOn, focusElement);
    }
  }

  /**
   * ✅ Rodar sempre após qualquer render/replace de HTML
   */
  postRender(parsedState) {
    try {
      this.attachOverlayListener();
      this.setHeaderCartIconAccessibility();

      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

      this.updateEmptyState(parsedState);
      this.retrapFocusIfActive();
    } catch (e) {
      // nunca deixar um erro de render travar a reabertura do drawer
      console.warn('[cart-drawer] postRender', e);
    }

    // ✅ debounced
    this.notifyCartMutated();
  }

  // =========================
  // Header Icon
  // =========================
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    // O clique para abrir e tratado por um listener DELEGADO no document (fim do arquivo),
    // que sempre abre o <cart-drawer> vivo — sobrevive a qualquer re-render do header/drawer.
  }

  // =========================
  // Open / Close
  // =========================
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');

      // ✅ evita postRender duplicado quando renderContents acabou de rodar
      if (this.__apxJustRendered) {
        this.__apxJustRendered = false;
      } else {
        this.postRender();
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        try {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

          if (containerToTrapFocusOn && focusElement) {
            trapFocus(containerToTrapFocusOn, focusElement);
          }
        } catch (e) {
          console.warn('[cart-drawer] focus trap', e);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    try {
      removeTrapFocus(this.activeElement);
    } catch (e) {}
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('overflow-hidden');
  }

  // =========================
  // Accessibility Summary
  // =========================
  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // =========================
  // Sections Rendering
  // =========================
  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner?.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // ✅ importante: roda pós-render depois que DOM novo já existe
    setTimeout(() => {
      try {
        this.postRender(parsedState);
        // ✅ marca que acabou de renderizar, pra open() não chamar postRender de novo
        this.__apxJustRendered = true;
      } catch (e) {
        console.warn('[cart-drawer] renderContents', e);
        this.__apxJustRendered = false;
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ============================================================================
   Abertura do carrinho via listener DELEGADO (à prova de re-render).
   Em vez de prender o clique a uma instância de <cart-drawer> que pode virar
   "morta" depois de um re-render, abrimos sempre o <cart-drawer> vivo do DOM.
   Isso corrige o "depois de atualizar o carrinho nao da mais pra abrir (F5)".
============================================================================ */
(function () {
  function openLiveDrawer(trigger) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(trigger || undefined);
      return true;
    }
    return false;
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('#cart-icon-bubble');
    if (!link) return;
    // só intercepta se houver um drawer; senão deixa o link ir pra /cart
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    openLiveDrawer(link);
  });

  document.addEventListener('keydown', function (event) {
    if (!event.code || event.code.toUpperCase() !== 'SPACE') return;
    var link = event.target.closest && event.target.closest('#cart-icon-bubble');
    if (!link) return;
    if (!document.querySelector('cart-drawer')) return;
    event.preventDefault();
    openLiveDrawer(link);
  });

  /* Rede de seguranca: depois de qualquer mutacao do carrinho, garante que
     nenhum spinner/estado de "carregando" fique preso por causa de re-render
     concorrente (tema + upsell + barra de progresso). */
  function clearStuckLoading() {
    try {
      document.querySelectorAll('.loading__spinner').forEach(function (s) {
        s.classList.add('hidden');
      });
      document.querySelectorAll('.cart__items--disabled').forEach(function (el) {
        el.classList.remove('cart__items--disabled');
      });
    } catch (e) {}
  }
  window.addEventListener('apx:cart-mutated', function () {
    setTimeout(clearStuckLoading, 1200);
  });
})();

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /**
   * ✅ update/remove:
   * - super.renderContents troca a .drawer__inner inteira
   * - então o pós-render precisa rodar depois do DOM novo existir
   * - e SEM spammar eventos (senão progressivo fica em “Carregando…”)
   */
  renderContents(parsedState) {
    super.renderContents(parsedState);

    // roda no próximo frame p/ garantir que o DOM novo já foi inserido
    requestAnimationFrame(() => {
      const drawer = document.querySelector('cart-drawer');

      if (drawer && typeof drawer.postRender === 'function') {
        drawer.postRender(parsedState);
      } else {
        // fallback: pelo menos atualiza o progressivo, mas com debounce via RAF
        try {
          window.dispatchEvent(new CustomEvent('apx:cart-mutated'));
        } catch (e) {}
      }
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);